// Word targets (SuiteModel/BulkMetadata.swift `WordTarget`, `TreeNode.words`; the app's LibraryModel+Outliner.swift
// `chapterTarget`) and the status workflow (ModelTypes.swift `MetadataField.option(after:/before:)`,
// LibraryModel+Collections.swift `stepStatus`).
import type { FieldOption, MetadataValue } from '../contract.ts';
import { storedWordTarget } from '../edit/editProject.ts';
import type { ArrangementTreeNode } from '../model/arrangement.ts';

/** `StandardField.wordTarget`. */
export const WORD_TARGET_FIELD = 'word-target';

/** `WordTarget`: targets at or above `limit` are nonsense, so they read as unset. */
export const WordTarget = {
  limit: 1_000_000_000,
  /** `value(of:)`: a positive whole number below the limit (a number, or the digits of text: "2,500"). */
  valueOf(value: MetadataValue | null | undefined): number | null {
    const stored = storedWordTarget(value ?? null);
    return stored?.type === 'number' ? stored.value : null;
  },
  /** `value(in:)`: the target set in a document's metadata. */
  value(metadata: Record<string, MetadataValue> | null | undefined): number | null {
    return WordTarget.valueOf(metadata?.[WORD_TARGET_FIELD]);
  },
  /** `stored`: what a setter stores (a valid target as a number, anything else cleared). */
  stored: storedWordTarget,
};

/** `TreeNode.words`: words in this node's own document and everything beneath it. */
export function treeWords(node: Pick<ArrangementTreeNode, 'node' | 'children'>, count: (fragmentID: string) => number): number {
  return (node.node.fragmentID !== undefined ? count(node.node.fragmentID) : 0) + node.children.reduce((n, c) => n + treeWords(c, count), 0);
}

/** The path from the top of the tree to `nodeID` (inclusive), or [] when it isn't there. */
export function pathTo(tree: ArrangementTreeNode[], nodeID: string): ArrangementTreeNode[] {
  const find = (nodes: ArrangementTreeNode[], trail: ArrangementTreeNode[]): ArrangementTreeNode[] | null => {
    for (const n of nodes) {
      if (n.node.id === nodeID) return [...trail, n];
      const hit = find(n.children, [...trail, n]);
      if (hit) return hit;
    }
    return null;
  };
  return find(tree, []) ?? [];
}

/** `chapterTarget`: the nearest document with a word target at or above the selection (usually its chapter), with the
 *  words written under it — Write's footer meter. */
export function chapterTarget(tree: ArrangementTreeNode[], selectedNodeID: string | null,
  metadataOf: (fragmentID: string) => Record<string, MetadataValue> | undefined, count: (fragmentID: string) => number,
  title: (node: ArrangementTreeNode['node']) => string): { nodeID: string; title: string; words: number; target: number } | null {
  if (selectedNodeID === null) return null;
  for (const node of pathTo(tree, selectedNodeID).reverse()) {
    const target = node.node.fragmentID !== undefined ? WordTarget.value(metadataOf(node.node.fragmentID)) : null;
    if (target !== null) return { nodeID: node.node.id, title: title(node.node), words: treeWords(node, count), target };
  }
  return null;
}

/** A card's or outliner row's progress against its target: words under it and the target, when it has one. */
export function targetProgress(node: ArrangementTreeNode, metadataOf: (fragmentID: string) => Record<string, MetadataValue> | undefined,
  count: (fragmentID: string) => number): { words: number; target: number; met: boolean } | null {
  const target = node.node.fragmentID !== undefined ? WordTarget.value(metadataOf(node.node.fragmentID)) : null;
  if (target === null) return null;
  const words = treeWords(node, count);
  return { words, target, met: words >= target };
}

// MARK: Status workflow

/** `option(after:)`: the option after `optionID` in the field's order (To Do → First Draft → … → Done): the first when
 *  nothing is chosen (or the choice isn't an option), null after the last. */
export function optionAfter(field: { options: FieldOption[] }, optionID: string | null | undefined): FieldOption | null {
  const i = optionID ? field.options.findIndex((o) => o.id === optionID) : -1;
  if (i < 0) return field.options[0] ?? null;
  return field.options[i + 1] ?? null;
}

/** `option(before:)`: the option before `optionID`; null before the first (or when nothing is chosen). */
export function optionBefore(field: { options: FieldOption[] }, optionID: string | null | undefined): FieldOption | null {
  const i = optionID ? field.options.findIndex((o) => o.id === optionID) : -1;
  return i > 0 ? field.options[i - 1] : null;
}

/** `stepStatus`: the option a document's status moves to, forward or back; null when it can't move (the app beeps). */
export function nextStatus(field: { options: FieldOption[] }, current: MetadataValue | undefined, forward = true): FieldOption | null {
  const id = current?.type === 'text' ? current.value : null;
  return forward ? optionAfter(field, id) : optionBefore(field, id);
}

/** Options in workflow order, for grouping by status (the outliner's Group by Status). */
export const workflowRank = (field: { options: FieldOption[] }, optionID: string | null | undefined) => {
  const i = optionID ? field.options.findIndex((o) => o.id === optionID) : -1;
  return i < 0 ? field.options.length : i;
};
